import './CreatePlaylistGroupModal.scss';
import React, { useEffect, useMemo, useState} from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import ErrorMessage from '../errorMessage/ErrorMessage';
import Modal from '../modal/Modal';
import Button from '../button/Button';
import InputField from '../inputField/InputField';
import { RESPONSE, ROUTES } from '../../js/_constants';
import Server from '../../js/config/_server';
import { apiPost } from '../../js/api';
import { userStore } from '../../store';
import {allCountries} from "../../js/countries";
import Dropdown from "../dropdown/Dropdown";
import {Playlist, PlaylistGroup} from "../../api/types";
import cx from "classnames";


type CreatePlaylistGroupModalProps = {
    onClose: () => void;
    isOpen: boolean;
    primaryButtonText?: string;
    onSuccess: (arg: { state: string; payload: string }) => void;
    playlistGroup?: PlaylistGroup;
};

const CreatePlaylistGroupModal = ({
    onClose,
    isOpen,
    primaryButtonText,
    onSuccess,
    playlistGroup
}: CreatePlaylistGroupModalProps) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { details } = userStore();

    const countries = useMemo(() => {
        const affiliatedCountries = details.affiliatedCountries as unknown;

        return (affiliatedCountries as typeof allCountries).map(userCountry => userCountry.name);
    }, [details.affiliatedCountries, allCountries]);

    const [isCreating, setIsCreating] = useState(false);

    const [playlistGroupTitle, setPlaylistGroupTitle] = useState('');
    const [playlistGroupDescription, setPlaylistGroupDecription] = useState('');
    const [market, setMarket] = useState('');

    const [error, setError] = useState('');

    const handleTitleChange = (value: string) => setPlaylistGroupTitle(value);
    const handleDecriptionChange = (value: string) => setPlaylistGroupDecription(value);
    const handleMarketChange = (_, __, value) => {
        const country = allCountries.find(country => country.name === value);
        if (country) {
            setMarket(country.iso3);
        }
    };

    const handleCreatePlaylistGroup = async (
        event: React.SyntheticEvent<HTMLButtonElement>
    ) => {
        setIsCreating(true);
        try {
            const url = Server.baseUrl + Server.services.playlistGroups.create;

            const options = {
                body: {
                    title: playlistGroupTitle,
                    description: playlistGroupDescription,
                    market: market,
                    ...(playlistGroup ? { uuid: playlistGroup.uuid } : {}),
                }
            };

            const createdPlaylistGroup = await apiPost(url, options);

            if ((event.target as HTMLButtonElement).name === 'create-and-navigate-button') {
                onSuccess({
                    state: 'redirecting',
                    payload: createdPlaylistGroup[RESPONSE.ID]
                });
                const id = createdPlaylistGroup[RESPONSE.ID].split('/');

                return navigate(`${ROUTES.PLAYLISTS}/groups/${id[6]}`);
            }

            return onSuccess({
                state: 'done',
                payload: createdPlaylistGroup[RESPONSE.ID]
            });
        } catch (err) {
            return setError(
                'There was an error while creating playlist group. Please try again!'
            );
        } finally {
            setIsCreating(false);
        }
    };

    const getMarketPreselection = () => {
        if (playlistGroup) {
            return countries.findIndex(country => country['@id'] === playlistGroup.country);
        }

        return -1;
    }

    useEffect(() => {
        if (playlistGroup) {
            setPlaylistGroupTitle(playlistGroup.title);
            setPlaylistGroupDecription(playlistGroup.description);
            setMarket(allCountries.find(c => c['@id'] === playlistGroup.country)?.iso3 ?? '');
        }
    }, [playlistGroup]);

    const renderPlaylistItem = (playlist: Playlist, index: number) => (
        <div key={`duplicate-playlist-${index}`} className={'playlist-item'}>
            <div>
                <b>
                    {playlist.title} {playlist?.language && (
                        <>({playlist.language})</>
                    )}
                </b>
            </div>
            <div><b>Duration: </b>{playlist.duration}</div>
            <div><p>{playlist.description}</p></div>
        </div>
    )
    const renderPlaylist = () => {
        return (
            <div className={'playlist-group-items'}>
                
                {playlistGroup?.items && playlistGroup.items
                // @ts-ignore
                .map((item: Playlist, index) => {
                    return renderPlaylistItem(item, index);
                })}
            </div>
        );
    }

    return (
        <Modal
            className={cx('create-playlist-group-modal', {
                'b-modal-duplicate' : playlistGroup
            })}
            show={isOpen}
            onClose={onClose}
        >
            <h4>{t('new_playlist_group_modal_title')}</h4>
            <div className="modal-content">
                <InputField
                    isFocusDisabled
                    label={t('title')}
                    name="title"
                    onValueChangeHandler={handleTitleChange}
                    value={playlistGroupTitle}
                />
                <InputField
                    isFocusDisabled
                    label={t('description')}
                    name="description"
                    inputType="input"
                    onValueChangeHandler={handleDecriptionChange}
                    value={playlistGroupDescription}
                />
                <div className="target">
                    <Dropdown
                        label={t('target_market')}
                        name="target"
                        options={countries}
                        onSelect={handleMarketChange}
                        preselection={getMarketPreselection()}
                    />
                </div>

                {playlistGroup && renderPlaylist()}
            </div>
            <div className="modal-footer">
                {!playlistGroup && <Button
                    name="modal-primary-button"
                    text={primaryButtonText || t('save')}
                    clickHandler={handleCreatePlaylistGroup}
                    secondaryButton
                    isLoading={isCreating}
                    disabled={playlistGroupTitle.length === 0 || market.length === 0}
                />}
                <Button
                    name="create-and-navigate-button"
                    text={t('new_playlist_group_redirect_to')}
                    clickHandler={handleCreatePlaylistGroup}
                    isLoading={isCreating}
                    disabled={playlistGroupTitle.length === 0 || market.length === 0}
                />
            </div>
            {error && <ErrorMessage message={error} />}
        </Modal>
    );
};

export default CreatePlaylistGroupModal;